import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip'
import React, { useState } from 'react'
import styled from 'styled-components'

import { CitationDocumentModal } from './CitationDocumentModal'

interface Citation {
  entryId: string
  fileRefPath: string
  pageNumber: number
  section: string
  quotations: string[]
}

interface CitationTooltipProps {
  citation: Citation
  siteID?: string
}

const CitationLink = styled.a`
  margin-left: 0.25rem;
  margin-right: 0.25rem;
  cursor: pointer;
  color: ${({ theme }) => theme.forecast.primary};
  font-weight: 600;
  text-decoration: none;
  font-size: 0.75em;
  vertical-align: baseline;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`

const TooltipContentWrapper = styled.div`
  max-width: 20rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const TooltipField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`

const TooltipLabel = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.75rem;
`

const TooltipValue = styled.div`
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 0.75rem;
  word-break: break-word;
  line-height: 1.3;
`

const Quotation = styled.div`
  margin-top: 0.125rem;
  padding-left: 0.5rem;
  border-left: 0.125rem solid ${({ theme }) => theme.forecast.border};
  color: ${({ theme }) => theme.forecast.tertiaryText};
  font-style: italic;
  font-size: 0.75rem;
  line-height: 1.3;

  &:not(:last-child) {
    margin-bottom: 0.25rem;
  }
`

const abbreviateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength) + '...'
}

const getFileName = (path: string): string => {
  const parts = path.split('/')
  const fileName = parts[parts.length - 1]
  return fileName.replace(/\.[^/.]+$/, '')
}

export const CitationTooltip: React.FC<CitationTooltipProps> = ({
  citation,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const fileName = getFileName(citation.fileRefPath)
  const abbreviatedFile = abbreviateText(fileName, 15)
  const abbreviatedSection = abbreviateText(citation.section, 25)
  const citationLabel = `[${abbreviatedFile}, ${abbreviatedSection}, p.${citation.pageNumber}]`

  return (
    <>
      <Tooltip key={`${citation.entryId}`}>
        <TooltipTrigger asChild>
          <CitationLink onClick={() => setIsModalOpen(true)}>
            {citationLabel}
          </CitationLink>
        </TooltipTrigger>
        <TooltipContent side='top' key={`content-${citation.entryId}`}>
          <TooltipContentWrapper>
            <TooltipField>
              <TooltipLabel>File</TooltipLabel>
              <TooltipValue>{citation.fileRefPath}</TooltipValue>
            </TooltipField>
            <TooltipField>
              <TooltipLabel>Page {citation.pageNumber}</TooltipLabel>
              <TooltipValue>{citation.section}</TooltipValue>
            </TooltipField>
            {citation.quotations.length > 0 && (
              <TooltipField>
                <TooltipLabel>Quotations</TooltipLabel>
                {citation.quotations.map((quote, idx) => (
                  <Quotation key={idx}>&ldquo;{quote}&rdquo;</Quotation>
                ))}
              </TooltipField>
            )}
          </TooltipContentWrapper>
        </TooltipContent>
      </Tooltip>

      <CitationDocumentModal
        citation={citation}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  )
}
